/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Builds query parameters for paginated appointment list requests on ViewAppointments.
 * @module logic/appointments/appointmentListParams
 */

/** Appointment list status filter values mapped to API `estado` strings. */
const APPOINTMENT_STATUS_FILTER_MAP = {
  0: "notSeen",
  1: "seen",
  3: "approved",
  4: "rejected",
};

/**
 * Returns true when the user is a buyer (type 11 or active buyer flag).
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export const isBuyerUser = (user) =>
  user?.tipoDeUsuario === "11" || Boolean(user?.compradorActivo);

/**
 * Builds API query params for the appointments table on ViewAppointments.
 * @param {Object} options
 * @param {number} options.page
 * @param {number} options.limit
 * @param {string} [options.debouncedSearch]
 * @param {Object|null} [options.selectedRequirement]
 * @param {Object|null} [options.userData]
 * @param {Object|null} [options.selectedBuyerCompany]
 * @param {Object|null} [options.selectedBuyerExecutive]
 * @param {Object|null} [options.selectedStatusFilter]
 * @param {Object|null} [options.selectedRequirementsFilter]
 * @returns {Object}
 */
export const buildAppointmentListParams = ({
  page,
  limit,
  debouncedSearch,
  selectedRequirement,
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  selectedStatusFilter,
  selectedRequirementsFilter,
}) => {
  const params = { page, limit };

  if (debouncedSearch) {
    params.search = debouncedSearch;
  }

  if (selectedRequirement?.value) {
    params.idRequerimiento = selectedRequirement.value;
  }

  if (userData && isBuyerUser(userData)) {
    params.buyerUserId = userData._id || localStorage.getItem("userId");
  } else {
    if (selectedBuyerCompany?.value) {
      params.buyerCompanyId = selectedBuyerCompany.value;
    }
    if (selectedBuyerExecutive?.value) {
      params.buyerUserId = selectedBuyerExecutive.value;
    }
  }

  if (selectedStatusFilter && selectedStatusFilter.value !== null) {
    if (selectedStatusFilter.value === 2) {
      params.meGusta = true;
    } else {
      const targetStatus =
        APPOINTMENT_STATUS_FILTER_MAP[selectedStatusFilter.value];
      if (targetStatus) {
        params.estado = targetStatus;
      }
    }
  }

  if (
    selectedRequirementsFilter &&
    selectedRequirementsFilter.value !== null
  ) {
    params.requirementsFulfilled = selectedRequirementsFilter.value === 0;
  }

  return params;
};
